import React, { useRef, useEffect, useContext, useState, useCallback } from "react";
import "./AutoResizingTextarea.scss";
import { EonUiIcon } from "@eon-ui/eon-ui-components-react";
import { useTranslation } from "@me8eon/translation";
import ImageUploader from "./ImageUploader";
import { EnvironmentConfig } from "@me8eon/config";
import { useUserData } from "@me8eon/react_authentication";
import { useAiWithRag } from "../ai/contexts/ai.rag.context";
import { ChosenChatbotSource } from "../ai/hooks/useChatbotIndices.hook";
import { AutoWidthSelect } from "@me8eon/auto_width_select";
import { SampleSelector, SourceSelectorRef } from "@me8eon/source_selector"
import { envName } from "../../util/common-util";
// import { Icon } from "@me8eon/icons";

interface Props {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  inputSubmit: (e?: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  placeholder?: string;
  id: string;
  isSearchFocused?: boolean;
  hasSearchData?: boolean;
  onFocus?: () => void;
  setSuggestedQuestions: (q: string[]) => void;
  suggestedQuestions: string[];
  isResetImage: boolean
  isReverse?: boolean
  setIsResetImage: (q: boolean) => void
}

const AutoResizingTextarea: React.FC<Props> = ({
  id,
  isReverse,
  setSuggestedQuestions,
  suggestedQuestions,
  inputSubmit,
  hasSearchData,
  value,
  onChange,
  placeholder = "Ask E.ON",
  onFocus,
  isSearchFocused,
  isResetImage,
  setIsResetImage,
}) => {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const selectorRef = useRef<SourceSelectorRef>(null);
  const t = useTranslation();
  const { featureFlagEnabled } = useUserData();
  const { chosenSource } = useAiWithRag()
  const [selectorResult, setSelectorResult] = useState<any>(null);
  placeholder = t("chatBot.searchAsk") || placeholder;

  const handleSelectorChange = useCallback((res: any) => {
    console.log("SourceSelector change", res);
    setSelectorResult(res);
  }, []);

  const resize = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight - 24}px`;
    }
  };

  useEffect(() => {
    resize();
    window.addEventListener("resize", resize);
    const timeoutId = setTimeout(() => {
      if (!window.location.pathname.includes("/ai")) {
        textareaRef.current?.focus();
      }
    }, 500);
    return () => {
      window.removeEventListener("resize", resize)
      clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => {
    resize();
  }, [value]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (value.length > 0) {
        setIsResetImage(true);
        inputSubmit(e);
      }
    }
  };

  return (
    <div
      className={`autoresize-textarea input-wrapper ${isSearchFocused ? "input-wrapper--focused" : ""
        } ${isReverse ? "input-wrapper--rev" : "input-wrapper--normal"
        } ${hasSearchData && isSearchFocused ? "input-wrapper--has-list" : ""
        } ${suggestedQuestions.length > 0 && isSearchFocused ? "input-wrapper--has-list" : ""}`}
      id={id}
    >
      <textarea
        ref={textareaRef}
        value={value}
        style={{ height: "auto" }}
        onChange={(e) => {
          onChange(e);
          resize();
        }}
        onKeyDown={handleKeyDown}
        onFocus={onFocus}
        placeholder={placeholder}
        className="expanding-textarea"
      />
      <div className="image-button-wrapper">
        <div style={{ display: "flex", gap: "8px" }}>
          {/* <Icon name="custom_agent" /> */}
          {(featureFlagEnabled) && (
            <SampleSelector
              ref={selectorRef}
              onChange={handleSelectorChange}
              defaultValue={chosenSource}
            />
          )}
          <ImageUploader
            isResetImage={isResetImage}
            setSuggestedQuestions={setSuggestedQuestions}
            resetChangeTrigger={(flag) => setIsResetImage(flag)}
          />
        </div>
        <div style={{ display: "flex", alignItems: "center" }}>
          <button
            className={`send-button`}
            onClick={(e) => {
              setIsResetImage(true);
              inputSubmit();
            }}
            disabled={value.trim().length < 1}
          >
            <EonUiIcon
              title={t("chatBot.sendbtn")}
              key={"sendbtn"}
              size="small"
              name="send"
              style={{ color: value.trim().length < 1 ? "#cccbca" : "#ea1b0a" }}
            />
          </button>
        </div>
      </div>
      <div style={{ textAlign: "left", marginLeft: "8px", marginBottom: "4px" }}>
        {selectorResult && selectorResult.selectedSourceId !== "all" && (
          <span
            onClick={() => selectorRef.current?.open()}
            style={{ cursor: "pointer" }}
          >
            Source: {selectorResult.selectedSourceName}
          </span>
        )}
      </div>
    </div >
  );
};

export default AutoResizingTextarea;
